import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { CircleAlert, Loader2 } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { z } from 'zod'
import { AuthFrame } from '@/components/auth-frame'
import { PasswordInput } from '@/components/password-input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { meQuery, useSignIn } from '@/lib/session'
import { useTitle } from '@/lib/use-title'

export const Route = createFileRoute('/login')({
  validateSearch: z.object({ redirect: z.string().optional() }),
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery)
    if (me) throw redirect({ to: me.mustChangePassword ? '/change-password' : '/' })
  },
  component: LoginPage,
})

function LoginPage() {
  useTitle('Sign in')
  const signIn = useSignIn()
  const navigate = useNavigate()
  const { redirect: next } = Route.useSearch()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    const { user } = await signIn.mutateAsync({ email: email.trim(), password })
    const safeNext = next?.startsWith('/') && !next.startsWith('//') ? next : '/'
    navigate({ to: user.mustChangePassword ? '/change-password' : safeNext, replace: true })
  }

  return (
    <AuthFrame title="Sign in to HCD Portal" intro="Use the email address HCD gave you.">
      <form onSubmit={(e) => void onSubmit(e).catch(() => {})} noValidate>
        <FieldGroup className="gap-5">
          {signIn.error && (
            <Alert variant="destructive" role="alert">
              <CircleAlert />
              <AlertDescription>{signIn.error.message}</AlertDescription>
            </Alert>
          )}
          <Field>
            <FieldLabel htmlFor="email">Email</FieldLabel>
            <Input
              id="email"
              type="email"
              inputMode="email"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11 text-base md:text-[15px]"
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="password">Password</FieldLabel>
            <PasswordInput
              id="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <Button
            type="submit"
            className="h-11 w-full text-[15px]"
            disabled={signIn.isPending || !email || !password}
          >
            {signIn.isPending && <Loader2 className="animate-spin" />}
            {signIn.isPending ? 'Signing in' : 'Sign in'}
          </Button>
        </FieldGroup>
      </form>
      <p className="mt-6 text-sm text-muted-foreground">
        <a href="/forgot-password" className="underline underline-offset-4">
          Forgot your password?
        </a>
      </p>
    </AuthFrame>
  )
}
